import { useFormContext } from "react-hook-form";

import { Accordion } from "ui/accordion";
import { Box } from "ui/box";
import { AlignmentField } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/alignment-field";
import { FormSelect } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/form-select";
import { ThemeColorSelect } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/theme-color-select";

import { getFontWeightOptions } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.helpers";

import type { BeeFont } from "types/bee";
import type {
  EmailConfigurationFormValues,
  ThemeColorOption,
  TypographySectionName,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  FIELDS_SX,
  ROTATE_180_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import {
  FALLBACK_FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  LINE_HEIGHT_OPTIONS,
  TYPOGRAPHY_SECTIONS,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import Icons from "assets";

type TypographySectionProps = {
  beeFonts: BeeFont[];
  colorOptions: ThemeColorOption[];
  disabled: boolean;
};

const TEXT_ALIGNMENT_OPTIONS = [
  { label: <Icons.TextAlignLeft role="img" aria-label="Left" />, value: "left" },
  { label: <Icons.TextAlignCenter role="img" aria-label="Center" />, value: "center" },
  {
    label: (
      <Box component="span" sx={ROTATE_180_SX}>
        <Icons.TextAlignLeft role="img" aria-label="Right" />
      </Box>
    ),
    value: "right",
  },
  { label: <Icons.TextAlignJustified role="img" aria-label="Justify" />, value: "justify" },
];

const TypographyFields = ({
  name,
  colorLabel,
  beeFonts,
  colorOptions,
  disabled,
}: TypographySectionProps & { name: TypographySectionName; colorLabel: string }) => {
  const { getValues, setValue, watch } = useFormContext<EmailConfigurationFormValues>();

  const fontOptions = beeFonts
    .filter((font): font is BeeFont & { value: string } => !!font.value)
    .map(({ name: fontName, value }) => ({ label: fontName, value }));
  const selectedFont = beeFonts.find(({ value }) => value === watch(`typography.${name}.fontFamily`));
  const fontWeightOptions = getFontWeightOptions(selectedFont);

  /** A weight the new font does not have falls back to its first, as the legacy page did. */
  const handleFontFamilyChange = (fontValue: string) => {
    const weights = getFontWeightOptions(beeFonts.find(({ value }) => value === fontValue));

    if (!weights.some(({ value }) => value === getValues(`typography.${name}.fontWeight`))) {
      setValue(`typography.${name}.fontWeight`, weights[0]?.value ?? "", { shouldDirty: true });
    }
  };

  return (
    <Box sx={FIELDS_SX}>
      <FormSelect
        name={`typography.${name}.fontFamily`}
        label="Font family"
        options={fontOptions}
        disabled={disabled}
        onValueChange={handleFontFamilyChange}
      />
      <FormSelect
        name={`typography.${name}.fallbackFontFamily`}
        label="Fallback font family"
        options={FALLBACK_FONT_FAMILY_OPTIONS}
        disabled={disabled}
      />
      <FormSelect name={`typography.${name}.fontSize`} label="Font size" options={FONT_SIZE_OPTIONS} disabled={disabled} />
      {name !== "buttonLabel" && (
        <AlignmentField name={`typography.${name}.alignment`} options={TEXT_ALIGNMENT_OPTIONS} disabled={disabled} />
      )}
      <FormSelect
        name={`typography.${name}.lineHeight`}
        label="Line height"
        options={LINE_HEIGHT_OPTIONS}
        disabled={disabled}
      />
      <FormSelect
        name={`typography.${name}.fontWeight`}
        label="Font weight"
        options={fontWeightOptions}
        disabled={disabled}
      />
      <ThemeColorSelect name={`typography.${name}.textColor`} label={colorLabel} options={colorOptions} disabled={disabled} />
      {name === "paragraph" && (
        <ThemeColorSelect name="typography.paragraph.linkColor" label="Link color" options={colorOptions} disabled={disabled} />
      )}
    </Box>
  );
};

export const TypographySection = (props: TypographySectionProps) =>
  TYPOGRAPHY_SECTIONS.map(({ name, title, colorLabel }, index) => (
    <Accordion key={name} title={title} defaultExpanded={index === 0}>
      <TypographyFields name={name} colorLabel={colorLabel} {...props} />
    </Accordion>
  ));
